import { apiRequest } from './api.js';
import firebaseService from './firebaseService';

// Proverb API Service - Integrated Firebase & Backend
export const proverbApiService = {
  // Get all proverbs from Firebase (primary with 102 proverbs)
  async getAllProverbsFromFirebase() {
    try {
      console.log('🔥 Fetching proverbs from Firebase...');
      const proverbs = await firebaseService.proverbs.getAllProverbs();
      console.log(`✅ Found ${proverbs.length} proverbs in Firebase`);
      return { proverbs, source: 'firebase' };
    } catch (error) {
      console.error('❌ Failed to fetch proverbs from Firebase:', error);
      throw new Error(`Failed to load proverbs from Firebase: ${error.message}`);
    }
  },

  // Get all proverbs with fallback strategy
  async getAllProverbs(page = 1, limit = 100, search = '', language = '') {
    try {
      // Try Firebase first (primary source with 102 proverbs)
      return await this.getAllProverbsFromFirebase();
    } catch (firebaseError) {
      console.warn('⚠️ Firebase failed, trying backend...', firebaseError.message);
      
      try {
        // Fallback to backend
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          ...(search && { search }),
          ...(language && { language })
        });

        const response = await apiRequest(`/proverbs?${params}`);
        
        if (response.proverbs) {
          return {
            proverbs: response.proverbs,
            pagination: response.pagination,
            source: 'backend'
          };
        }
        
        return { proverbs: response, pagination: null, source: 'backend' };
      } catch (backendError) {
        console.error('❌ Both Firebase and backend failed:', backendError);
        
        // Final fallback to mock data
        return {
          proverbs: "mockProverbs",
          source: 'mock',
          error: 'Both Firebase and backend unavailable'
        };
      }
    }
  },

  // Get a single proverb by ID (try Firebase first, then backend)
  async getProverbById(id) {
    try {
      console.log(`🔍 Fetching proverb ${id} from Firebase...`);
      return await firebaseService.proverbs.getProverbById(id);
    } catch (firebaseError) {
      console.warn('⚠️ Firebase failed, trying backend...', firebaseError.message);
      
      try {
        const response = await apiRequest(`/proverbs/${id}`);
        return response;
      } catch (backendError) {
        console.error('❌ Both sources failed for proverb:', id);
        throw new Error(`Failed to load proverb: ${backendError.message}`);
      }
    }
  },

  // Create a new proverb (save to both Firebase and backend if possible)
  async createProverb(proverbData) {
    try {
      console.log('📝 Creating proverb in Firebase:', proverbData.chinese_proverb);
      
      // Save to Firebase first
      const firebaseResult = await firebaseService.proverbs.addProverb({
        chinese_proverb: proverbData.chinese_proverb,
        pinyin: proverbData.pinyin,
        english_translation: proverbData.english_translation,
        english_explanation: proverbData.english_explanation || '',
        english_equivalent: proverbData.english_equivalent || '',
        french_translation: proverbData.french_translation || '',
        french_explanation: proverbData.french_explanation || '',
        french_equivalent: proverbData.french_equivalent || '',
        arabic_translation: proverbData.arabic_translation || '',
        arabic_Explanation: proverbData.arabic_Explanation || '',
        status: 'active',
        source: 'dashboard'
      });

      const firebaseId = firebaseResult.firebaseId || firebaseResult;
      const proverbId = firebaseResult.proverbId; // Use the auto-generated incremental ID
      
      console.log(`✅ Proverb created in Firebase: ID #${proverbId} (Firebase doc: ${firebaseId})`);
      
      // Validate that we got a proper numeric ID
      if (!proverbId || typeof proverbId !== 'number') {
        console.error('⚠️ Warning: Proverb was saved but did not get a proper numeric ID');
      }
      
      // Log activity
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.id) {
        await firebaseService.utils.logUserActivity(
          user.id.toString(), 
          user.name, 
          'PROVERB_CREATED', 
          { 
            proverbId: proverbId, 
            firebaseId: firebaseId,
            proverb: proverbData.chinese_proverb,
            source: 'firebase'
          }
        );
      }

      // Try to also save to backend (optional)
      try {
        const backendResponse = await apiRequest('/proverbs', {
          method: 'POST',
          body: JSON.stringify({
            ...proverbData,
            firebase_id: firebaseId,
            proverb_id: proverbId,
            status: 'active'
          }),
        });
        console.log('✅ Also saved to backend:', backendResponse.id);
      } catch (backendError) {
        console.warn('⚠️ Backend save failed, but Firebase succeeded:', backendError.message);
      }

      return { 
        id: firebaseId, 
        proverbId: proverbId,
        source: 'firebase' 
      };
    } catch (error) {
      console.error('❌ Failed to create proverb:', error);
      throw new Error(`Failed to create proverb: ${error.message}`);
    }
  },

  // Update an existing proverb
  async updateProverb(id, updateData) {
    try {
      console.log('📝 Updating proverb:', id);
      
      // Update in Firebase
      await firebaseService.proverbs.updateProverb(id, {
        chinese_proverb: updateData.chinese_proverb,
        pinyin: updateData.pinyin,
        english_translation: updateData.english_translation,
        english_explanation: updateData.english_explanation || '',
        english_equivalent: updateData.english_equivalent || '',
        french_translation: updateData.french_translation || '',
        french_explanation: updateData.french_explanation || '',
        french_equivalent: updateData.french_equivalent || '',
        arabic_translation: updateData.arabic_translation || '',
        arabic_Explanation: updateData.arabic_Explanation || ''
      });

      console.log('✅ Proverb updated in Firebase:', id);
      
      // Log activity
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.id) {
        await firebaseService.utils.logUserActivity(
          user.id.toString(), 
          user.name, 
          'PROVERB_UPDATED', 
          { 
            proverbId: id, 
            changes: Object.keys(updateData),
            source: 'firebase'
          }
        );
      }

      return { success: true, source: 'firebase' };
    } catch (error) {
      console.error('❌ Failed to update proverb:', error);
      throw new Error(`Failed to update proverb: ${error.message}`);
    }
  },

  // Delete a proverb
  async deleteProverb(id) {
    try {
      console.log('🗑️ Deleting proverb:', id);
      
      // Delete from Firebase
      await firebaseService.proverbs.deleteProverb(id);
      console.log('✅ Proverb deleted from Firebase:', id);
      
      // Log activity
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.id) {
        await firebaseService.utils.logUserActivity(
          user.id.toString(), 
          user.name, 
          'PROVERB_DELETED', 
          { proverbId: id, source: 'firebase' }
        );
      }

      return true;
    } catch (error) {
      console.error('❌ Failed to delete proverb:', error);
      throw new Error(`Failed to delete proverb: ${error.message}`);
    }
  },

  // Voice-related methods using Firebase Storage
  async uploadVoiceForProverb(proverbId, audioFile, metadata = {}) {
    try {
      console.log('🎤 Uploading voice for proverb:', proverbId);
      
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const voiceResult = await firebaseService.voice.uploadVoice(proverbId, audioFile, {
        ...metadata,
        uploadedBy: user.id?.toString(),
        uploaderName: user.name
      });
      
      console.log('✅ Voice uploaded successfully:', voiceResult.id);
      
      // Log the activity
      if (user.id) {
        await firebaseService.utils.logUserActivity(
          user.id.toString(), 
          user.name, 
          'VOICE_UPLOADED', 
          { 
            proverbId, 
            voiceId: voiceResult.id,
            filename: audioFile.name,
            size: audioFile.size 
          }
        );
      }
      
      return voiceResult;
    } catch (error) {
      console.error('❌ Failed to upload voice:', error);
      throw new Error(`Failed to upload voice: ${error.message}`);
    }
  },

  async getVoiceForProverb(proverbId) {
    try {
      return await firebaseService.voice.getVoiceByProverbId(proverbId);
    } catch (error) {
      console.error('❌ Failed to get voice for proverb:', error);
      return [];
    }
  },

  async deleteVoice(voiceId) {
    try {
      const result = await firebaseService.voice.deleteVoice(voiceId);
      
      // Log the activity
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.id) {
        await firebaseService.utils.logUserActivity(
          user.id.toString(), 
          user.name, 
          'VOICE_DELETED', 
          { voiceId }
        );
      }
      
      return result;
    } catch (error) {
      console.error('❌ Failed to delete voice:', error);
      throw new Error(`Failed to delete voice: ${error.message}`);
    }
  },

  // Activity methods
  async getAllActivities() {
    try {
      return await firebaseService.activities.getAllActivities();
    } catch (error) {
      console.error('❌ Failed to get activities:', error);
      return [];
    }
  },

  async getRecentActivities(limit = 50) {
    try {
      return await firebaseService.activities.getRecentActivities(limit);
    } catch (error) {
      console.error('❌ Failed to get recent activities:', error);
      return [];
    }
  },

  // Dashboard stats
  async getDashboardStats() {
    try {
      return await firebaseService.utils.getDashboardStats();
    } catch (error) {
      console.error('❌ Failed to get dashboard stats:', error);
      return {
        totalProverbs: 0,
        totalActivities: 0,
        totalVoices: 0,
        recentActivities: []
      };
    }
  },

  // Search proverbs (legacy backend method)
  async searchProverbs(searchTerm = '', language = '', page = 1, limit = 100) {
    try {
      const params = new URLSearchParams({
        q: searchTerm,
        page: page.toString(),
        limit: limit.toString(),
        ...(language && { language })
      });

      const response = await apiRequest(`/proverbs/search?${params}`);
      
      return {
        proverbs: response.proverbs || response,
        pagination: response.pagination,
        searchTerm: response.searchTerm,
        language: response.language
      };
    } catch (error) {
      console.error('❌ Failed to search proverbs:', error);
      throw new Error(`Failed to search proverbs: ${error.message}`);
    }
  }
};

export default proverbApiService;
